<script setup>
/**
 * PipelineLossReasonSetting — Fase 4.5 frontend
 *
 * Simple toggle for `pipeline.settings['require_loss_reason']`.
 * Backend (PipelineCard#mark_as_lost!) raises 422 when on and reason
 * is blank. MarkAsLostModal reflects the requirement with an asterisk
 * on the reason label (separate component edit).
 */
import { nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  pipeline: { type: Object, required: true },
  readonly: { type: Boolean, default: false },
});
const emit = defineEmits(['change']);

const { t } = useI18n();
const required = ref(props.pipeline?.settings?.require_loss_reason === true);
const isResetting = ref(false);

watch(required, val => {
  if (isResetting.value || props.readonly) return;
  emit('change', { require_loss_reason: val });
});

watch(
  () => props.pipeline?.id,
  async () => {
    isResetting.value = true;
    required.value = props.pipeline?.settings?.require_loss_reason === true;
    await nextTick();
    isResetting.value = false;
  }
);
</script>

<template>
  <section class="flex items-start justify-between gap-4">
    <div class="min-w-0">
      <h4 class="text-label font-semibold text-n-slate-12">
        {{ t('PIPELINE.SETTINGS.REQUIRE_LOSS_REASON.TITLE') }}
      </h4>
      <p class="text-label-small text-n-slate-11 mt-0.5">
        {{ t('PIPELINE.SETTINGS.REQUIRE_LOSS_REASON.DESCRIPTION') }}
      </p>
    </div>
    <Switch
      v-model="required"
      :disabled="readonly"
      :aria-label="t('PIPELINE.SETTINGS.REQUIRE_LOSS_REASON.TITLE')"
      class="shrink-0"
    />
  </section>
</template>
